import * as React from "react";
import { useState, useEffect } from "react";
import { actionCreators as uiActionCreators } from "ui/store";
import { RouteComponentProps, withRouter } from "react-router";
import { actionCreators } from "page/calendar/store";
import { DefaultCulture, NavigationComponent } from "ui/constants";
import ControlsContainer from "../controls-container";
import * as styles from "./extra-pages.css";
import {
    CalendarState,
    ExtraPageResource,
    ExtraPageInfo,
    ExtraPage,
} from "burde";
import { useExtraPages } from "hooks/use-extra-pages";
import ExtraPageSelector from "../extra-page-selector/extra-page-selector";
import { useResources } from "contexts/resource-context";
import Button from "content/button/button";
import SelectedExtraPages from "../selected-extra-pages/selected-extra-pages";
import { CalendarType } from "svg/constants";
import { getStmExtraPageById } from "UI/extra-page-service";

type Props = {
    calendar: CalendarState;
    previewedExtraPage: ExtraPage | null;
    activeComponent: NavigationComponent;
    addedExtraPages: number[];
    children: React.ReactNode;
} & typeof actionCreators &
    typeof uiActionCreators;

const ExtraPages = ({
    calendar,
    activeComponent,
    updateExtraPageCollection,
    previewedExtraPage,
    addedExtraPages,
    updatePreviewedExtraPage,
    children,
}: Props & RouteComponentProps) => {
    const [showExtraPageSelector, setShowExtraPageSelector] = useState<boolean>(
        false,
    );
    const [
        selectedResourceGroup,
        setSelectedResourceGroup,
    ] = useState<ExtraPageResource | null>(null);

    const { extraPages, maxNumberOfPages, numberOfPagesLeft } = useExtraPages(
        calendar,
    );

    useEffect(() => {
        const currentExtraPages = [];
        // Remove extra pages not in the response
        if (extraPages !== undefined) {
            for (let i = 0; i < calendar.extraPages.length; i++) {
                const extraPageId = calendar.extraPages[i];
                if (findExtraPageById(extraPageId) !== null) {
                    currentExtraPages.push(calendar.extraPages[i]);
                }
            }
            if (currentExtraPages.length !== calendar.extraPages.length) {
                updateExtraPageCollection(currentExtraPages);
            }

            if (extraPages !== undefined && extraPages.length > 0) {
                setSelectedResourceGroup(extraPages[0]);
            }
        }
    }, [extraPages]);

    const resources = useResources();

    const findExtraPageById = (extraPageId: number) => {
        if (extraPages === undefined) {
            return null;
        }

        for (let i = 0; i < extraPages.length; i++) {
            for (let a = 0; a < extraPages[i].content.length; a++) {
                if (extraPages[i].content[a].id === extraPageId) {
                    return extraPages[i].content[a];
                }
            }
        }

        return null;
    };

    const onUpdatePreviewedExtraPage = (id: number) => {
        getStmExtraPageById(
            calendar.culture !== null ? calendar.culture : DefaultCulture,
            calendar.calendarSize,
            id,
        ).then((response) => {
            if (response !== undefined) {
                updatePreviewedExtraPage(response);
            }
        });
    };

    const onUpdateExtraPages = (extraPages: number[]) => {
        updateExtraPageCollection(extraPages);
        setShowExtraPageSelector(false);
        updatePreviewedExtraPage(null);
    };

    if (previewedExtraPage !== null) {
        return (
            <>
                <div className={styles.controlsInner}>
                    <ControlsContainer Title={previewedExtraPage.title}>
                        <p>{previewedExtraPage.description}</p>
                        <div className={styles.buttonContainer}>
                            <Button
                                disabled={numberOfPagesLeft < 1}
                                onClick={() =>
                                    onUpdateExtraPages([
                                        ...addedExtraPages,
                                        previewedExtraPage.id,
                                    ])
                                }
                                text={resources["ExtraPages_Add"]}
                            />
                        </div>
                    </ControlsContainer>
                </div>

                <div
                    className={styles.navigation}
                    onClick={() => updatePreviewedExtraPage(null)}>
                    <i
                        className={["fa fa-times", styles.closeSelector].join(
                            " ",
                        )}
                    />
                </div>
            </>
        );
    }

    if (showExtraPageSelector) {
        return (
            <>
                <div className={styles.controlsInner}>
                    <ControlsContainer Title={""}>
                        <ExtraPageSelector
                            extraPageResources={extraPages}
                            addedExtraPages={addedExtraPages}
                            onUpdatePreviewedExtraPage={
                                onUpdatePreviewedExtraPage
                            }
                            onUpdateExtraPages={onUpdateExtraPages}
                            numberOfPagesLeft={numberOfPagesLeft}
                            selectedResourceGroup={selectedResourceGroup}
                            setSelectedResourceGroup={setSelectedResourceGroup}
                        />
                    </ControlsContainer>
                </div>

                <div
                    className={styles.navigation}
                    onClick={() => setShowExtraPageSelector(false)}>
                    <i
                        className={["fa fa-times", styles.closeSelector].join(
                            " ",
                        )}
                    />
                </div>
            </>
        );
    }

    return (
        <>
            <div className={styles.controlsInner}>
                <ControlsContainer Title={activeComponent.resourceKey}>
                    <p>
                        {calendar.calendarType !== CalendarType.Notebook
                            ? resources["ExtraPages_Description"].replace(
                                  "{0}",
                                  `${maxNumberOfPages}`,
                              )
                            : resources[
                                  "Notebook_ExtraPagesDescription"
                              ].replace("{0}", `${maxNumberOfPages}`)}
                    </p>
                    <SelectedExtraPages
                        selectedExtraPages={addedExtraPages}
                        extraPageResources={extraPages}
                        onUpdateExtraPages={onUpdateExtraPages}
                        numberOfPagesLeft={numberOfPagesLeft}
                        onUpdatePreviewedExtraPage={onUpdatePreviewedExtraPage}
                    />
                    <hr />
                    <div className={styles.addExtraPage}>
                        <Button
                            disabled={numberOfPagesLeft < 1}
                            onClick={() => {
                                setShowExtraPageSelector(true);
                            }}
                            text={resources["ExtraPages_Add"]}
                            iconClass={"fa fa-plus"}
                        />
                    </div>
                    <hr />
                    <div className={styles.remainingPages}>
                        <p>
                            <strong>
                                {maxNumberOfPages - numberOfPagesLeft}/
                                {maxNumberOfPages}
                            </strong>
                        </p>
                        {resources["ExtraPages_RemainingPages"].replace(
                            "{0}",
                            `${numberOfPagesLeft}`,
                        )}
                    </div>
                </ControlsContainer>
            </div>

            {children}
        </>
    );
};

const RoutedExtraPages = withRouter(ExtraPages);
export { RoutedExtraPages as ExtraPages };
